import type {
  OrchestratorConfig,
  ConfigValidationResult,
  OverlayLayout,
  OverlayBehavior
} from '../types';
import {
  AGENT_NAME_PATTERN,
  LAYOUT_CONSTRAINTS,
  VALID_POSITIONS,
  VALID_SANDBOX_ATTRIBUTES,
  VALID_REFERRER_POLICIES
} from '../constants';

/**
 * Parse PublicChatWidget URL to extract parameters
 * @param url Complete PublicChatWidget URL
 * @returns Parsed parameters or null if invalid
 */
function parsePublicChatUrl(url: string): {
  baseUrl: string;
  organizationId: string;
  tenantId: string;
  agentName: string;
  overlayName?: string;
} | null {
  try {
    const urlObj = new URL(url);

    // Pattern: /public/agent-chat/{org}/{tenant}/{agent}[/{overlay}]
    const pathMatch = urlObj.pathname.match(
      /^\/public\/agent-chat\/([^\/]+)\/([^\/]+)\/([^\/]+)(?:\/([^\/]+))?$/
    );

    if (!pathMatch) return null;

    return {
      baseUrl: urlObj.origin,
      organizationId: decodeURIComponent(pathMatch[1]),
      tenantId: decodeURIComponent(pathMatch[2]),
      agentName: decodeURIComponent(pathMatch[3]),
      overlayName: pathMatch[4] ? decodeURIComponent(pathMatch[4]) : undefined
    };
  } catch {
    return null;
  }
}

/**
 * ConfigManager handles configuration validation, merging, and management
 */
export class ConfigManager {
  private config: OrchestratorConfig;
  private defaultConfig: Partial<OrchestratorConfig>;

  constructor(initialConfig: Partial<OrchestratorConfig> = {}) {
    this.defaultConfig = this.getDefaultConfig();

    // Parse publicChatUrl BEFORE merging with defaults
    const processedConfig = this.parsePublicChatUrlIfProvided(initialConfig);

    this.config = this.mergeWithDefaults(processedConfig);
  }

  private getDefaultConfig(): Partial<OrchestratorConfig> {
    return {
      autoInit: true,
      debug: false,
      forceOverlay: true,
      layout: {
        position: 'bottom-right',
        width: 400,
        height: 600,
        launcherSize: 60,
        launcherOffset: { x: 20, y: 20 },
        animations: true,
        responsive: true,
        zIndex: 9999,
        borderRadius: 16,
        shadow: true,
        showLauncherLabel: false,
        launcherLabel: 'Chat with us',
        persistState: true,
        hideOnMobile: false,
      },
      behavior: {
        autoOpen: false,
        autoOpenDelay: 3000,
        minimizable: true,
        persistState: true,
        soundEnabled: false,
        voiceInputEnabled: true,
      },
      iframe: {
        sandbox: [
          'allow-scripts',
          'allow-same-origin', 
          'allow-forms',
          'allow-popups',
          'allow-presentation'
        ],
        allowFullscreen: false,
        referrerPolicy: 'strict-origin-when-cross-origin'
      }
    };
  }

  /**
   * Get current configuration
   */
  getConfig(): OrchestratorConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<OrchestratorConfig>): ConfigValidationResult {
    const newConfig = this.mergeConfigs(this.config, updates);
    const validation = this.validateConfig(newConfig);
    
    if (validation.isValid) {
      this.config = newConfig;
    }
    
    return validation;
  }

  /**
   * Update layout configuration only
   */
  updateLayout(layout: Partial<OverlayLayout>): ConfigValidationResult {
    const updatedConfig = {
      ...this.config,
      layout: this.mergeConfigs(this.config.layout || {}, layout)
    };
    
    const validation = this.validateConfig(updatedConfig);
    
    if (validation.isValid) {
      this.config = updatedConfig;
    }
    
    return validation;
  }

  /**
   * Get layout configuration
   */
  getLayout(): OverlayLayout {
    return this.mergeConfigs(this.defaultConfig.layout || {}, this.config.layout || {}) as OverlayLayout;
  }

  /**
   * Get behavior configuration
   */
  getBehavior(): OverlayBehavior {
    return this.mergeConfigs(this.defaultConfig.behavior || {}, this.config.behavior || {}) as OverlayBehavior;
  }

  /**
   * Validate configuration
   */
  validateConfig(config: Partial<OrchestratorConfig>): ConfigValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!config.agentName) {
      errors.push('agentName is required');
    }

    // Validate agentName format
    if (config.agentName && !AGENT_NAME_PATTERN.test(config.agentName)) {
      errors.push('agentName must contain only alphanumeric characters, hyphens, and underscores');
    }

    // Validate API base URL
    if (config.apiBaseUrl) {
      try {
        new URL(config.apiBaseUrl);
      } catch (e) {
        errors.push('apiBaseUrl must be a valid URL');
      }
    }

    // Validate base URL
    if (config.baseUrl) {
      try {
        new URL(config.baseUrl);
      } catch (e) {
        errors.push('baseUrl must be a valid URL');
      }
    }

    // Validate layout configuration
    if (config.layout) {
      const layoutValidation = this.validateLayout(config.layout);
      errors.push(...layoutValidation.errors);
      warnings.push(...layoutValidation.warnings);
    }

    // Validate iframe configuration
    if (config.iframe) {
      const iframeValidation = this.validateIframeConfig(config.iframe);
      errors.push(...iframeValidation.errors);
      warnings.push(...iframeValidation.warnings);
    }

    // Performance warnings
    if (config.debug === true) {
      warnings.push('Debug mode enabled - disable for production');
    }

    // Validate publicChatUrl format if provided
    if (config.publicChatUrl) {
      const parsed = parsePublicChatUrl(config.publicChatUrl);

      if (!parsed) {
        errors.push(`publicChatUrl '${config.publicChatUrl}' must match pattern: /public/agent-chat/{organizationId}/{tenantId}/{agentName}[/{overlayName}]`);
      }
    }

    // Validate public chat mode
    if (config.publicChatMode) {
      // Require organizationId and tenantId for public chat mode
      if (!config.organizationId) {
        errors.push('organizationId is required when publicChatMode is enabled');
      }
      if (!config.tenantId) {
        errors.push('tenantId is required when publicChatMode is enabled');
      }

      // Validate publicChatBaseUrl if provided
      if (config.publicChatBaseUrl) {
        try {
          new URL(config.publicChatBaseUrl);
        } catch (e) {
          errors.push('publicChatBaseUrl must be a valid URL');
        }
      }
    }

    // Validate channel chat mode
    if (config.channelChatUrl) {
      // Validate URL format
      try {
        const url = new URL(config.channelChatUrl);
        const pathname = url.pathname;

        // Check if it matches either encrypted or plain channel URL pattern
        const isEncrypted = /^\/chat\/c\/[^/]+$/.test(pathname);
        const isPlain = /^\/chat\/[^/]+\/[^/]+\/channel\/[^/]+$/.test(pathname);

        if (!isEncrypted && !isPlain) {
          warnings.push(
            `channelChatUrl '${config.channelChatUrl}' does not match expected patterns:\n` +
            `  - Encrypted: /chat/c/{token}\n` +
            `  - Plain: /chat/{org}/{tenant}/channel/{channelId}`
          );
        }
      } catch (e) {
        errors.push('channelChatUrl must be a valid URL');
      }

      // Warn if both publicChatUrl and channelChatUrl are provided
      if (config.publicChatUrl) {
        warnings.push('Both publicChatUrl and channelChatUrl provided - channelChatUrl will take precedence');
      }
    }

    // Security warnings
    if (config.allowedOrigins && config.allowedOrigins.includes('*')) {
      warnings.push('Wildcard origin allowed - consider restricting for security');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Reset configuration to defaults
   */
  resetToDefaults(): void {
    this.config = this.mergeWithDefaults({});
  }

  /**
   * Export configuration as JSON
   */
  exportConfig(): string {
    return JSON.stringify(this.config, null, 2);
  }

  /**
   * Import configuration from JSON
   */
  importConfig(json: string): ConfigValidationResult {
    try {
      const imported = JSON.parse(json);
      return this.updateConfig(imported);
    } catch (error) {
      return {
        isValid: false,
        errors: [`Invalid JSON: ${error instanceof Error ? error.message : String(error)}`],
        warnings: []
      };
    }
  }

  /**
   * Get configuration summary for debugging
   */
  getConfigSummary(): Record<string, any> {
    return {
      agentName: this.config.agentName,
      overlayName: this.config.overlayName,
      apiBaseUrl: this.config.apiBaseUrl,
      baseUrl: this.config.baseUrl,
      organizationId: this.config.organizationId,
      tenantId: this.config.tenantId,
      autoInit: this.config.autoInit,
      debug: this.config.debug,
      layout: {
        position: this.config.layout?.position,
        width: this.config.layout?.width,
        height: this.config.layout?.height,
        launcherSize: this.config.layout?.launcherSize,
        animations: this.config.layout?.animations
      },
      hasToken: !!(this.config as OrchestratorConfig & { token?: string }).token,
      hasApiKey: !!(this.config as OrchestratorConfig & { apiKey?: string }).apiKey,
      hasOrganizationId: !!this.config.organizationId,
      hasTenantId: !!this.config.tenantId
    };
  }

  /**
   * Parse publicChatUrl and extract parameters if provided
   */
  private parsePublicChatUrlIfProvided(config: Partial<OrchestratorConfig>): Partial<OrchestratorConfig> {
    if (!config.publicChatUrl) {
      return config;
    }

    const parsed = parsePublicChatUrl(config.publicChatUrl);

    if (!parsed) {
      // Invalid URL - return as-is, validation will catch this later
      return config;
    }

    // Create a new config with parsed values (don't override explicitly provided values)
    return {
      ...config,
      publicChatMode: true,
      organizationId: config.organizationId || parsed.organizationId,
      tenantId: config.tenantId || parsed.tenantId,
      agentName: config.agentName || parsed.agentName,
      overlayName: config.overlayName || parsed.overlayName,
      publicChatBaseUrl: config.publicChatBaseUrl || parsed.baseUrl
    };
  }

  /**
   * Merge configuration with defaults
   */
  private mergeWithDefaults(config: Partial<OrchestratorConfig>): OrchestratorConfig {
    const merged = this.mergeConfigs(this.defaultConfig, config);

    // Ensure required fields have values
    if (!merged.agentName && !config.agentName) {
      merged.agentName = 'demo-agent'; // Fallback
    }

    return merged as OrchestratorConfig;
  }

  /**
   * Deep merge two configuration objects
   */
  private mergeConfigs<T extends Record<string, any>>(
    target: T, 
    source: Partial<T>
  ): T {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] !== undefined) {
        if (
          typeof source[key] === 'object' && 
          source[key] !== null && 
          !Array.isArray(source[key]) &&
          typeof target[key] === 'object' && 
          target[key] !== null
        ) {
          // Deep merge objects
          result[key] = this.mergeConfigs(
            target[key] as Record<string, any>, 
            source[key] as Partial<Record<string, any>>
          ) as T[Extract<keyof T, string>];
        } else {
          // Direct assignment for primitives and arrays
          result[key] = source[key] as T[Extract<keyof T, string>];
        }
      }
    }
    
    return result;
  }

  /**
   * Validate layout configuration
   */
  private validateLayout(layout: Partial<OverlayLayout>): { errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate position
    if (layout.position && !VALID_POSITIONS.includes(layout.position)) {
      errors.push(`Invalid position: ${layout.position}. Must be one of: ${VALID_POSITIONS.join(', ')}`);
    }

    // Validate dimensions
    if (layout.width !== undefined) {
      if (typeof layout.width !== 'number' || 
          layout.width < LAYOUT_CONSTRAINTS.WIDTH_MIN || 
          layout.width > LAYOUT_CONSTRAINTS.WIDTH_MAX) {
        errors.push(`Width must be a number between ${LAYOUT_CONSTRAINTS.WIDTH_MIN} and ${LAYOUT_CONSTRAINTS.WIDTH_MAX}`);
      }
    }

    if (layout.height !== undefined) {
      if (typeof layout.height !== 'number' || 
          layout.height < LAYOUT_CONSTRAINTS.HEIGHT_MIN || 
          layout.height > LAYOUT_CONSTRAINTS.HEIGHT_MAX) {
        errors.push(`Height must be a number between ${LAYOUT_CONSTRAINTS.HEIGHT_MIN} and ${LAYOUT_CONSTRAINTS.HEIGHT_MAX}`);
      }
    }

    // Validate launcher size
    if (layout.launcherSize !== undefined) {
      if (typeof layout.launcherSize !== 'number' || 
          layout.launcherSize < LAYOUT_CONSTRAINTS.LAUNCHER_SIZE_MIN || 
          layout.launcherSize > LAYOUT_CONSTRAINTS.LAUNCHER_SIZE_MAX) {
        errors.push(`Launcher size must be a number between ${LAYOUT_CONSTRAINTS.LAUNCHER_SIZE_MIN} and ${LAYOUT_CONSTRAINTS.LAUNCHER_SIZE_MAX}`);
      }
    }

    // Validate offsets
    if (layout.launcherOffset) {
      if (typeof layout.launcherOffset.x !== 'number' || 
          layout.launcherOffset.x < LAYOUT_CONSTRAINTS.OFFSET_MIN || 
          layout.launcherOffset.x > LAYOUT_CONSTRAINTS.OFFSET_MAX) {
        errors.push(`Launcher offset X must be a number between ${LAYOUT_CONSTRAINTS.OFFSET_MIN} and ${LAYOUT_CONSTRAINTS.OFFSET_MAX}`);
      }
      if (typeof layout.launcherOffset.y !== 'number' || 
          layout.launcherOffset.y < LAYOUT_CONSTRAINTS.OFFSET_MIN || 
          layout.launcherOffset.y > LAYOUT_CONSTRAINTS.OFFSET_MAX) {
        errors.push(`Launcher offset Y must be a number between ${LAYOUT_CONSTRAINTS.OFFSET_MIN} and ${LAYOUT_CONSTRAINTS.OFFSET_MAX}`);
      }
    }

    // Validate z-index
    if (layout.zIndex !== undefined) {
      if (typeof layout.zIndex !== 'number' || 
          layout.zIndex < LAYOUT_CONSTRAINTS.Z_INDEX_MIN || 
          layout.zIndex > LAYOUT_CONSTRAINTS.Z_INDEX_MAX) {
        errors.push(`Z-index must be a number between ${LAYOUT_CONSTRAINTS.Z_INDEX_MIN} and ${LAYOUT_CONSTRAINTS.Z_INDEX_MAX}`);
      }
      
      if (layout.zIndex < LAYOUT_CONSTRAINTS.Z_INDEX_WARNING_THRESHOLD) {
        warnings.push(`Z-index below ${LAYOUT_CONSTRAINTS.Z_INDEX_WARNING_THRESHOLD} may cause layering issues with other page elements`);
      }
    }

    // Validate border radius
    if (layout.borderRadius !== undefined) {
      if (typeof layout.borderRadius !== 'number' || 
          layout.borderRadius < LAYOUT_CONSTRAINTS.BORDER_RADIUS_MIN || 
          layout.borderRadius > LAYOUT_CONSTRAINTS.BORDER_RADIUS_MAX) {
        errors.push(`Border radius must be a number between ${LAYOUT_CONSTRAINTS.BORDER_RADIUS_MIN} and ${LAYOUT_CONSTRAINTS.BORDER_RADIUS_MAX}`);
      }
    }

    return { errors, warnings };
  }

  /**
   * Validate iframe configuration
   */
  private validateIframeConfig(iframe: NonNullable<OrchestratorConfig['iframe']>): { errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate sandbox attributes
    if (iframe.sandbox) {
      const invalidAttrs = iframe.sandbox.filter(attr => 
        !VALID_SANDBOX_ATTRIBUTES.includes(attr as typeof VALID_SANDBOX_ATTRIBUTES[number])
      );
      if (invalidAttrs.length > 0) {
        errors.push(`Invalid sandbox attributes: ${invalidAttrs.join(', ')}`);
      }

      // Security warnings
      if (iframe.sandbox.includes('allow-same-origin') && iframe.sandbox.includes('allow-scripts')) {
        warnings.push('Combining allow-same-origin and allow-scripts reduces security isolation');
      }
    }

    // Validate referrer policy
    if (iframe.referrerPolicy && !VALID_REFERRER_POLICIES.includes(iframe.referrerPolicy as typeof VALID_REFERRER_POLICIES[number])) {
      errors.push(`Invalid referrer policy: ${iframe.referrerPolicy}. Must be one of: ${VALID_REFERRER_POLICIES.join(', ')}`);
    }

    return { errors, warnings };
  }
}